import { GetDocumentFromDocumentum } from '@/interfaces/documents';
import { Document } from '@/interfaces/requirement';
import { DocumentumService } from '@/services/documentum/documentum.service';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { dateMustBeBeforeOrEqualThanToday } from '@/validators/dateMustBeBeforeOrEqualThanToday';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import JSZip from 'jszip';
import { concat } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-document-table[driverIsEmployee]',
  templateUrl: './document-table.component.html',
  styleUrls: ['./document-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentTableComponent {

  @Input() documents: Document[] = [];
  @Input() requirementId: number = 0;
  @Input() driverMdmid: string = '';
  @Input() driverIsEmployee: boolean = false;
  @Input() assetId: number = 0;
  @Input() marginTop: boolean = true;

  @Input() shouldShowDocumentLinks: boolean = false;
  @Input() shouldEditEffectiveDate: boolean = false;
  @Input() formArray = new FormArray([]);
  @Input() editableDocuments = false;

  errorOnDownloadIds: Record<number, string> = {};

  downloadingAllDocumentsPercent: number = 0; // between 0 and 100

  documentDateRangeError(index: number): string {
    const formGroupControl = this.getformGroupByRowIndex(index);

    const error = formGroupControl.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  documentDateFromError(index: number): string {
    const formGroupControl = this.getformGroupByRowIndex(index);

    const error = formGroupControl.controls.dateFrom.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  isEditingAnEntry: boolean = false;
  editingEntrys: boolean[] = [];

  constructor(
    private documentumService: DocumentumService,
    private cdr: ChangeDetectorRef,
  ) { }

  getformGroupByRowIndex(index: number): FormGroup {
    let requestedIndex = -1;
    this.formArray.value.some(({ index: rowIndex }: any, formArrayIndex: number) => {
      if (rowIndex === index) {
        requestedIndex = formArrayIndex;
      }
    });
    return this.formArray.at(requestedIndex) as FormGroup;
  }

  onEditAnEntry(index: number, documentId: number) {
    const selectedDocument = this.documents.find((document) => document.id === documentId);
    const controls: Record<string, FormControl> = {
      id: new FormControl(documentId), // Estos se dejan dentro del control para hacer validaciones
      documentRequiredTypeId: new FormControl(selectedDocument?.documentRequiredTypeId), // Estos se dejan dentro del control para hacer validaciones
      index: new FormControl(index), // Estos se dejan dentro del control para hacer validaciones
      file: new FormControl({
        status: 'initial',
        description: '',
        fileUrl: selectedDocument?.url,
        fileName: selectedDocument?.description,
      }),
    };

    const validators: any[] = [];

    if (selectedDocument?.dateEmit) {
      controls.defaultDateEmit = new FormControl(selectedDocument?.dateEmit); // Estos se dejan dentro del control para hacer validaciones
      controls.dateEmit = new FormControl(selectedDocument?.dateEmit);
    }
    else {
      controls.dateFrom = new FormControl(selectedDocument?.dateEmit, [dateMustBeBeforeOrEqualThanToday()]);
      controls.dateUntil = new FormControl(selectedDocument?.dateUntil);
      controls.defaultDateFrom = new FormControl(selectedDocument?.dateFrom); // Estos se dejan dentro del control para hacer validaciones
      controls.defaultDateUntil = new FormControl(selectedDocument?.dateUntil); // Estos se dejan dentro del control para hacer validaciones
      validators.push(dateEndMustBeAfterDateStart('dateFrom', 'dateUntil'));
    }

    this.formArray.push(new FormGroup(controls, validators));
    this.editingEntrys[index] = true;
  }

  asFormControl(abstractControl: AbstractControl): FormControl {
    return abstractControl as FormControl;
  }

  getTextOfEntity(text?: string): string {
    if (!text) {
      return '';
    }
    if (!(text.includes('Empleados:') || text.includes('Contratistas:'))) {
      return text;
    }
    const searchKey = this.driverIsEmployee ? 'Empleados:' : 'Contratistas:';

    if (!text.includes(searchKey)) {
      return '';
    }
    const textList = text.split('\\n');

    return textList.find((x) => x.includes(searchKey))!.replace(searchKey, '').trim();
  }

  downloadAllFiles() {
    this.downloadingAllDocumentsPercent = 1;
    const requests$ = this.documents.filter((x) => x.name !== 'BHP.pdf').map((document) => {
      const params: GetDocumentFromDocumentum = {
        mdmid: this.driverMdmid,
        requirementId: this.requirementId,
        documentId: document.id,
        assetId: this.assetId,
      };

      return this.documentumService.downloadDocumentFile(params).pipe(
        map<Blob, [string, Blob]>((x) => [`${document.description}_${document.documentRequiredTypeId}.pdf`, x]),
      );
    });

    if (requests$.length === 0) {
      this.downloadingAllDocumentsPercent = 0;
      this.cdr.detectChanges();
      return;
    }

    const files: [string, Blob][] = [];
    concat(...requests$).subscribe(({
      next: (document) => {
        files.push(document);
        this.downloadingAllDocumentsPercent = Math.round((files.length / requests$.length) * 100);
        this.cdr.detectChanges();
        if (files.length === requests$.length) {
          this.saveFilesToZip(files);
        }
      },
    }));

  }

  saveFilesToZip(documents: [string, Blob][]) {
    const zipFile = new JSZip();

    documents.map(([name, file]) => {
      let sanitizedName = name.replace('.pdf', '');
      sanitizedName = sanitizedName.replace(/[^\w\s]/gi, '');
      sanitizedName += '.pdf';

      zipFile.file(sanitizedName, file);
    });

    zipFile.generateAsync({ type: 'blob' })
      .then((content) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(content);

        a.href = objectUrl;
        a.download = `${this.requirementId}_documents.zip`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      });
  }

}
